import React from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { css, Theme } from '@emotion/react';
import { mixins } from '../../core/styles/theme/default';
import { Grid, GridItem } from '../../helpers';
import { Link, ModuleHeader } from '../../primitives';
import Spacer from '../../helpers/Spacer';
import Slider from '../Slider';

const StyledGrid = styled(Grid)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(-16)}`,
      medium: `${rem(-24)}`,
      ultra: `${rem(-32)}`,
    },
  })}
`;

const StyledGridItem = styled(GridItem)`
  ${mixins.responsiveStyles({
    'margin-bottom': {
      zero: `${rem(16)}`,
      medium: `${rem(24)}`,
      ultra: `${rem(32)}`,
    },
  })}
`;

const StyledTeaserCard = styled.div`
  height: 100%;

  &[data-type] {
    & > .spacer {
      display: none;
    }

    & > * {
      margin: 0;
      width: 100%;
      max-width: 100%;
      height: 100%;
      box-sizing: border-box;
    }
  }

  &[data-type='ContentfulPublication'] > div {
    padding: 0;
  }

  &[data-type='ContentfulStoryHub'] > article {
    padding: 0;

    & > div {
      padding: 0;
    }
  }

  &[data-parent='slider'] {
    min-height: ${rem(340)};

    & > * {
      min-height: inherit;
    }

    &[data-type='ContentfulEventHub'],
    &[data-type='ContentfulPublication'] {
      min-height: 0;
    }
  }
`;

const StyledMoreLink = styled(Link)(
  ({ theme }: { theme?: Theme }) => css`
    position: relative;
    z-index: 1;

    .m-richtext & {
      text-decoration: none;
    }

    ${theme?.stylesExtension?.genericTeaserModule?.moreLink}
  `,
);

const GenericTeaserModule = ({
  title,
  lead,
  items,
  displayMode = 'Slider',
  visibleItemsCount = '2',
  overviewLink,
}: GenericTeaserModuleProps) => {
  if (!items?.length) return null;

  return (
    <div className="m-genericteasermodule">
      <Grid columns={12}>
        <GridItem size={{ zero: 12, ultra: 8 }} push={{ zero: 0, ultra: 2 }}>
          {title && (
            <>
              <ModuleHeader title={title} lead={lead} />
              <Spacer space="gamma" />
            </>
          )}
        </GridItem>
      </Grid>
      <Grid columns={12}>
        <GridItem
          size={visibleItemsCount === '2' ? { zero: 12, ultra: 8 } : 12}
          push={visibleItemsCount === '2' ? { zero: 0, ultra: 2 } : 0}
        >
          {displayMode === 'Slider' ? (
            <Slider showHiddenSlides slidesPerView={+visibleItemsCount}>
              {items.map((item: GenericTeaserModuleItemProps, i) => (
                <StyledTeaserCard
                  key={`${item.id}-${i}`}
                  data-type={item.type}
                  data-parent="slider"
                >
                  {item.content}
                </StyledTeaserCard>
              ))}
            </Slider>
          ) : (
            <StyledGrid columns={12}>
              {items.map((item: GenericTeaserModuleItemProps, i) => (
                <StyledGridItem
                  key={`${item.id}-${i}`}
                  size={{
                    zero: 12,
                    medium: 6,
                    wide: 12 / +visibleItemsCount,
                  }}
                >
                  <StyledTeaserCard data-type={item.type} data-parent="grid">
                    {item.content}
                  </StyledTeaserCard>
                </StyledGridItem>
              ))}
            </StyledGrid>
          )}
        </GridItem>
      </Grid>
      <Grid columns={12}>
        <GridItem size={{ zero: 12, ultra: 8 }} push={{ zero: 0, ultra: 2 }}>
          {overviewLink ? (
            <>
              <Spacer space="epsilon" />
              <StyledMoreLink
                linkText={overviewLink.text}
                link={{
                  href: overviewLink.href,
                  title: overviewLink.title,
                }}
                variant="internal"
              />
            </>
          ) : null}
        </GridItem>
      </Grid>
    </div>
  );
};

export type GenericTeaserModuleItemProps = {
  type: string;
  id: string;
  content: React.ReactNode;
};

export type GenericTeaserModuleProps = {
  title?: string;
  lead?: string;
  items: GenericTeaserModuleItemProps[];
  displayMode?: 'Slider' | 'Grid';
  visibleItemsCount?: '2' | '3' | '4';
  overviewLink?: {
    text: string;
    title: string;
    href: string;
  };
};

export default GenericTeaserModule;
